import { ReactNode } from 'react';
import { useHref } from 'react-router-dom';
import { Box, Cover, Tooltip } from '@optiaxiom/react';

import { rowDisabled, rowLink } from './RowLinkCover.css';

type RowLinkCoverProps = {
  href?: string;
  onActivate: () => void;
  children: ReactNode;
  // When true, the row renders as a visually disabled overlay — no click handler,
  // faded content, not-allowed cursor. Used by callers that want to signal the row
  // is present but not actionable (e.g. private-execution rows viewed by non-owners).
  disabled?: boolean;
  // When set, shown on hover over a disabled row so the reason for the disabled
  // state is discoverable. Ignored unless ``disabled`` is true.
  disabledTooltip?: string;
};

export function RowLinkCover({
  href,
  onActivate,
  children,
  disabled,
  disabledTooltip,
}: RowLinkCoverProps) {
  const resolvedHref = useHref(href ?? '');
  if (disabled) {
    const box = <Box className={rowDisabled}>{children}</Box>;
    return (
      <Cover asChild>
        {disabledTooltip ? (
          <Tooltip content={disabledTooltip} delayDuration={0}>
            {box}
          </Tooltip>
        ) : (
          box
        )}
      </Cover>
    );
  }
  if (!href) {
    return (
      <Cover asChild>
        <Box onClick={onActivate} cursor="pointer">
          {children}
        </Box>
      </Cover>
    );
  }
  return (
    <Cover asChild>
      <a
        href={resolvedHref}
        className={rowLink}
        onClick={e => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
          e.preventDefault();
          onActivate();
        }}
      >
        {children}
      </a>
    </Cover>
  );
}
